/**
 * Early-access signup.
 *
 * Deliberately tiny and deliberately separate from the game: this page needs
 * no wallet, no chain client and no ethers, so it is its own Vite entry rather
 * than a route inside core-game. A visitor who has been handed a code should
 * not download 800KB of WalletConnect to type it in.
 *
 * It does import the game's stylesheet rather than carrying its own. That is
 * the whole reason the page looks like Scratchy and will keep looking like it:
 * a copied set of tokens is a copy that drifts, which is exactly how
 * index-production.html ended up seven commits behind its twin (MIGRATION.md).
 */
import "./styles.css";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const code = $<HTMLInputElement>("eaCode");
const xIn = $<HTMLInputElement>("eaX");
const tgIn = $<HTMLInputElement>("eaTg");
const submit = $<HTMLButtonElement>("eaSubmit");
const state = $("eaState");
const done = $("eaDone");
const fields = document.querySelector<HTMLElement>(".ea-fields")!;
const lede = document.querySelector<HTMLElement>(".ea-lede")!;

/**
 * Swap the form for the confirmation.
 *
 * One function rather than the same four lines at each call site, and the lede
 * is part of what it hides: "Enter your invite code and tell us where to find
 * you" left standing above a completed signup is an instruction to do something
 * there is no longer any way to do.
 */
function showDone(message: string): void {
  $("eaDoneMsg").textContent = message;
  lede.style.display = "none";
  fields.style.display = "none";
  submit.style.display = "none";
  state.textContent = "";
  done.classList.add("on");
}

/* Same origin in the single-service deployment; Vite's dev server needs the
   relayer named explicitly. Mirrors chain/config.ts rather than inventing a
   second convention. */
const API = (import.meta.env.VITE_RELAYER_URL ?? "") as string;

function say(msg: string, kind: "err" | "info" = "err"): void {
  state.textContent = msg;
  state.className = `ea-state ${kind === "err" ? "err" : "info"}`;
}

function busy(on: boolean): void {
  submit.disabled = on;
  submit.textContent = on ? "Checking…" : "Claim my spot";
}

/* Codes are minted uppercase from an alphabet with no I/O/0/1. Upcasing as the
   player types means a lowercase paste is not a failed attempt against the
   rate limit — and the limit is tight enough that wasting attempts on
   formatting would be a real cost. */
code.addEventListener("input", () => {
  const at = code.selectionStart;
  code.value = code.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (at !== null) code.setSelectionRange(at, at);
});

/* Typing @ is the instinct, and the field already shows one. Stripping it here
   keeps the two from doubling up in the box. */
for (const el of [xIn, tgIn]) {
  el.addEventListener("input", () => {
    if (el.value.startsWith("@")) el.value = el.value.slice(1);
  });
}

async function claim(): Promise<void> {
  const body = { code: code.value.trim(), x: xIn.value.trim(), telegram: tgIn.value.trim() };

  if (!body.code) return say("Enter your access code.");
  if (!body.x && !body.telegram) return say("Add an X or Telegram handle so we can reach you.");

  busy(true);
  say("", "info");
  try {
    const res = await fetch(`${API}/early-access/redeem`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      /* The pass comes back as a cookie, which a cross-origin dev setup drops
         without this. */
      credentials: "include",
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}) as Record<string, unknown>);
    if (!res.ok) {
      say(String(data.error ?? "Something went wrong. Try again in a moment."));
      return;
    }

    const who = [data.x ? `@${data.x}` : "", data.telegram ? `@${data.telegram} on Telegram` : ""]
      .filter(Boolean)
      .join(" · ");
    showDone(
      data.alreadyRegistered
        ? `You were already on the list — ${who}. Nothing more to do.`
        : `We've got you: ${who}. Watch for a message from us.`
    );
  } catch {
    /* Network-level failure. The generic message is honest here: from the
       browser's side a DNS failure and a dead server look the same. */
    say("Couldn't reach the server. Check your connection and try again.");
  } finally {
    busy(false);
  }
}

submit.addEventListener("click", () => void claim());
for (const el of [code, xIn, tgIn]) {
  el.addEventListener("keydown", e => {
    if ((e as KeyboardEvent).key === "Enter") void claim();
  });
}

/* Someone who already holds a pass should not be asked to redeem again — they
   may no longer have the code, and re-entering it would be a dead end. Failing
   quietly is right: if the check cannot complete, the form is still usable. */
void (async () => {
  try {
    const res = await fetch(`${API}/early-access/status`, { credentials: "include" });
    const data = await res.json();
    if (data?.hasAccess) showDone("You already have access on this device.");
  } catch {
    /* no-op: the form below is the fallback */
  }
})();

/* A code can be handed out as a link, so /early-access?code=ABCD2345 fills the
   field. Only ever prefilled, never auto-submitted: the handles are still the
   person's to type, and a code spent by merely opening a link would be spent
   by every preview crawler that followed it. */
const fromUrl = new URLSearchParams(location.search).get("code");
if (fromUrl) code.value = fromUrl.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 32);
